import { PETS_APIS } from "@/api_framework/api_config";
import axios, { AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";

interface IUpdatePetWeightResponse {
  status: boolean;
  message: string;
  data?: {
    pet_id?: string;
    pet_weight?: string | null;
  } | null;
}

AxiosCurlirize(axios, () => {});

export const useUpdatePetWeightAPI = () => {
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const [isPetWeightUpdateInProgress, setIsPetWeightUpdateInProgress] = useState(false);

  const execute = useCallback(
    async (userId: string, petId: string, petWeight: string): Promise<boolean> => {
      if (!userId || !petId || !petWeight) {
        toast.error("Missing pet details to update weight.");
        return false;
      }

      setIsPetWeightUpdateInProgress(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const res: AxiosResponse<IUpdatePetWeightResponse> = await axios.patch(
          `${PETS_APIS?.UPDATE_PET?.baseURL ?? ""}${userId}/${petId}/`,
          { pet_weight: petWeight },
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (res.data?.status) {
          toast.success(res.data?.message || "Pet weight updated successfully.");
          return true;
        }

        toast.error(res.data?.message || "Unable to update pet weight.");
        return false;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.config?.["curlCommand"]) {
            sendCurl(error.config["curlCommand"]);
          }
          captureError(error);
          toast.error((error.response?.data as any)?.message || "Unable to update pet weight.");
          return false;
        }

        const err = error as Error;
        toast.error("Server Error: " + err.message);
        return false;
      } finally {
        setIsPetWeightUpdateInProgress(false);
      }
    },
    [captureError, configuration, sendCurl],
  );

  return {
    execute,
    isPetWeightUpdateInProgress,
  };
};
